import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { MLProjectStatus } from '@/utils/enums';

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const SelectionButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const SelectionButton = styled(Text)`
  &:disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const Wrap = styled.div`
  margin-top: 32px;
`;

const Card = styled.div`
  padding: 24px 16px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 32px;
`;

type LangButtonProps = {
  $active: boolean;
};

const LangButton = styled(Text).attrs({
  $type: 'smallBold',
})<LangButtonProps>`
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  transition: 0.2s ease-in-out all;

  ${({ theme, $active }) =>
    !$active &&
    css`
      &:hover {
        border-color: ${theme.colors.gray};
      }
    `}

  ${({ theme, $active }) =>
    $active &&
    css`
      color: ${theme.colors.purple};
      border-color: ${theme.colors.purple};
    `}

    &:disabled {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    border-color: ${({ theme }) => theme.colors.grayscaleLight};
    color: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

interface Props {
  projectId: number;
  existingLanguages: string[];
  projectStatus: MLProjectStatus;
}

const LanguagesSelection: FC<Props> = ({
  projectId,
  existingLanguages,
  projectStatus,
}) => {
  const queryClient = useQueryClient();

  const { data } = useQuery(
    ['ml_available_languages'],
    () => api.get<never, string[]>(`/v1/distribution/ml-available-languages`),
    {
      retry: 2,
      staleTime: Infinity,
    },
  );

  const languages = data ?? [];

  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const alerts = useAlerts();

  const onSubmit = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Generate translations for selected languages',
        selectedLanguages.join(', '),
      );

      if (!confirmed) {
        return;
      }

      setIsLoading(true);

      await api.post(`/v1/distribution/ml-projects/${projectId}/languages`, {
        languages: selectedLanguages,
      });

      await queryClient.invalidateQueries(['ml_projects']);

      alerts.success(
        'Translations is being generated using the help of ChatGPT',
      );

      setSelectedLanguages([]);
    } catch (_) {
      alerts.error();
    } finally {
      setIsLoading(false);
    }
  };

  const onLanguageClick = (lang: string) => {
    setSelectedLanguages((prev) => {
      if (prev.includes(lang)) {
        return prev.filter((l) => l !== lang);
      }

      return [...prev, lang];
    });
  };

  const onSelectAll = () => {
    setSelectedLanguages(
      languages.filter((lang) => !existingLanguages.includes(lang)),
    );
  };

  const onReset = async (shouldRegenerate: boolean) => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        shouldRegenerate
          ? 'Regenerate all translations'
          : 'Reset all translations',
      );

      if (!confirmed) {
        return;
      }

      setIsLoading(true);

      await api.put(`/v1/distribution/ml-projects/${projectId}/languages`, {
        should_regenerate: shouldRegenerate,
      });

      await queryClient.invalidateQueries(['ml_projects']);

      alerts.success(
        shouldRegenerate
          ? 'Translations is being regenerated using the help of ChatGPT'
          : 'Translations successfully deleted',
      );
    } catch {
      alerts.error();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Wrap>
      <Header>
        <Text $type="caption">Available Languages</Text>
        <SelectionButtons>
          <SelectionButton
            $type="smallHeadline"
            as="button"
            onClick={onSelectAll}
          >
            Select all
          </SelectionButton>
          <SelectionButton
            $type="smallHeadline"
            as="button"
            onClick={() => setSelectedLanguages([])}
            disabled={!selectedLanguages.length}
          >
            Clear
          </SelectionButton>
        </SelectionButtons>
      </Header>
      <Card>
        {languages.map((lang) => (
          <LangButton
            as="button"
            $active={selectedLanguages.includes(lang)}
            key={lang}
            onClick={() => onLanguageClick(lang)}
            disabled={existingLanguages.includes(lang)}
          >
            {lang}
          </LangButton>
        ))}
      </Card>
      {projectStatus !== MLProjectStatus.DRAFT && (
        <Buttons>
          <SubmitButton
            loading={isLoading}
            disabled={isLoading || !selectedLanguages.length}
            onClick={onSubmit}
            color="secondary"
          >
            Generate additional
          </SubmitButton>
        </Buttons>
      )}
      {projectStatus === MLProjectStatus.DRAFT && (
        <>
          {existingLanguages.length ? (
            <Buttons>
              <SubmitButton
                loading={isLoading}
                disabled={isLoading || !selectedLanguages.length}
                onClick={onSubmit}
                color="secondary"
              >
                Generate additional
              </SubmitButton>
              <SubmitButton
                loading={isLoading}
                disabled={isLoading}
                onClick={() => onReset(true)}
                color="secondary"
              >
                Regenerate results
              </SubmitButton>
              <SubmitButton
                loading={isLoading}
                disabled={isLoading}
                onClick={() => onReset(false)}
                color="danger"
              >
                Reset results
              </SubmitButton>
            </Buttons>
          ) : (
            <SubmitButton
              loading={isLoading}
              disabled={isLoading || !selectedLanguages.length}
              onClick={onSubmit}
            >
              Generate translations
            </SubmitButton>
          )}
        </>
      )}
    </Wrap>
  );
};

export default LanguagesSelection;
